import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
}

interface InnerProps extends Props {
  title: string;
  desc: string;
  reload: string;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends React.Component<InnerProps, State> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const { title, desc, reload } = this.props;
      return (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center min-h-[40vh]">
          <div className="size-16 rounded-2xl bg-destructive/10 flex items-center justify-center text-destructive mb-4 shadow-sm">
            <AlertTriangle className="size-8" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
          <p className="text-sm text-muted-foreground max-w-md mb-6">{desc}</p>
          <Button onClick={() => window.location.reload()} className="gap-2 shadow-sm">
            <RotateCcw className="w-4 h-4" />
            {reload}
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundary({ children }: Props) {
  const { t } = useTranslation();
  return (
    <ErrorBoundaryInner
      title={t('errorBoundary.title')}
      desc={t('errorBoundary.desc')}
      reload={t('errorBoundary.reload')}
    >
      {children}
    </ErrorBoundaryInner>
  );
}

export function RouteErrorElement() {
  const { t } = useTranslation();
  const error = useRouteError();
  console.error('Route error caught by RouteErrorElement:', error);

  let errorMessage = t('errorBoundary.desc');
  if (isRouteErrorResponse(error)) {
    errorMessage = `${error.status} ${error.statusText || error.data?.message || ''}`.trim();
  } else if (error instanceof Error && error.message) {
    errorMessage = error.message;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 flex flex-col items-center justify-center py-16 px-4 text-center font-sans selection:bg-[#017EBA]/20">
      <div className="size-16 rounded-2xl bg-destructive/10 flex items-center justify-center text-destructive mb-4 shadow-sm">
        <AlertTriangle className="size-8" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
        {t('errorBoundary.title')}
      </h2>
      <p className="text-sm text-muted-foreground max-w-md mb-6 break-words">
        {errorMessage || t('errorBoundary.desc')}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button onClick={() => window.location.reload()} className="gap-2 shadow-sm">
          <RotateCcw className="w-4 h-4" />
          {t('errorBoundary.reload')}
        </Button>
        <Button asChild variant="outline" className="gap-2">
          <Link to="/">
            <Home className="w-4 h-4" />
            {t('auth.goHome', 'Strona główna')}
          </Link>
        </Button>
      </div>
    </div>
  );
}
